import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface ExpenseResponse {
  record_id: string; expense_id: string; brain_id: string;
  service_name: string; category: string;
  amount_usd_cents: number; amount_ils_agorot: number; incurred_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8,
      padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
      alignItems: 'flex-start',
    }}>
      <span style={{
        fontSize: '8pt', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', paddingTop: 1,
      }}>
        {label}
      </span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', wordBreak: 'break-all', fontFamily: 'var(--omega-mono)' }}>
        {children}
      </span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceExpenseDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { expense_id = '' } = useParams<{ expense_id: string }>();

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'expenses', expense_id],
    queryFn: () => apiFetch<ExpenseResponse>(`/finance/expenses/${expense_id}`),
    refetchInterval: 30_000,
    enabled: !!expense_id,
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Expenses', to: '/omega/finance/expenses' },
        { label: expense_id.slice(0, 8) },
      ]} />

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', marginTop: 16 }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 16 }}>Error loading data.</div>}

      {data && (
        <>
          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <h1 style={{ margin: 0, fontSize: '13pt', fontWeight: 700, letterSpacing: '0.04em', fontFamily: 'var(--omega-mono)' }}>
              {data.expense_id.slice(0, 8)}
            </h1>
            <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 4 }}>
              {data.service_name}
            </div>
          </div>

          {/* Currency summary — dual display */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={{
              background: 'rgba(125,211,252,0.08)', border: '1px solid rgba(125,211,252,0.3)',
              borderRadius: 6, padding: '14px 18px', minWidth: 140,
            }}>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>USD</div>
              <div style={{ fontSize: '18pt', fontWeight: 700, color: '#7dd3fc' }}>{fmtUsd(data.amount_usd_cents)}</div>
            </div>
            <div style={{
              background: 'var(--omega-surface, rgba(255,255,255,0.04))', border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
              borderRadius: 6, padding: '14px 18px', minWidth: 140,
            }}>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>ILS</div>
              <div style={{ fontSize: '18pt', fontWeight: 700, color: 'var(--omega-ink)' }}>{fmtIls(data.amount_ils_agorot)}</div>
              <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>At exchange rate BOI</div>
            </div>
          </div>

          {/* Detail fields */}
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 6, padding: '0 16px', marginBottom: 20,
          }}>
            <FieldRow label="Expense ID">{data.expense_id}</FieldRow>
            <FieldRow label="Record ID">{data.record_id}</FieldRow>
            <FieldRow label="Brain ID">{data.brain_id}</FieldRow>
            <FieldRow label="Service">{data.service_name}</FieldRow>
            <FieldRow label="Category">{data.category}</FieldRow>
            <FieldRow label="Date">{fmtDate(data.incurred_at)}</FieldRow>
          </div>
        </>
      )}
    </div>
  );
}
